import type { DateRange } from "@/shared/hooks/useDateParams";
import type { EntryFilterState, EnumFilterField } from "./filters/types";
import type { Entry } from "./normalize";
import { getTimekeeperId } from "./timekeepers";

export type DateFilter = { from?: string; to?: string };

export function toYYYYMMDD(d: Date): string {
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

export function computeDateFilter(
	date: Date,
	dateRange: DateRange,
): DateFilter {
	if (dateRange.from || dateRange.to) {
		return {
			from: dateRange.from ? toYYYYMMDD(dateRange.from) : undefined,
			to: dateRange.to ? toYYYYMMDD(dateRange.to) : undefined,
		};
	}
	const single = toYYYYMMDD(date);
	return { from: single, to: single };
}

const ENUM_ACCESSORS: Record<EnumFilterField, (e: Entry) => string | null> = {
	timekeeper: (e) => getTimekeeperId(e),
	status: (e) => e.status,
	application: (e) => e.application ?? null,
	matter: (e) => e.matter?.id ?? "",
	client: (e) => e.matter?.client ?? "",
	billableCategory: (e) => e.billableCategory,
	task: (e) => e.codes?.task ?? "",
	activity: (e) => e.codes?.activity ?? "",
};

const FILTER_FIELDS = [
	"application",
	"matter",
	"client",
	"billableCategory",
	"task",
	"activity",
] as const;

export function makeEntryPredicate(
	filters: EntryFilterState,
	dateFilter: DateFilter,
): (e: Entry) => boolean {
	const status = filters.status ?? [];
	const timekeeper = filters.timekeeper ?? null;
	const fieldSelections = FILTER_FIELDS.map(
		(field) => [field, filters[field]] as const,
	);

	return (e: Entry): boolean => {
		const d = e.date;
		if (dateFilter.from && d < dateFilter.from) return false;
		if (dateFilter.to && d > dateFilter.to) return false;

		const entryStatus = ENUM_ACCESSORS.status(e);
		if (entryStatus == null || !status.includes(entryStatus)) return false;

		if (timekeeper) {
			const entryTimekeeper = ENUM_ACCESSORS.timekeeper(e);
			if (entryTimekeeper !== timekeeper) return false;
		}

		for (const [field, values] of fieldSelections) {
			if (values === undefined) continue;
			if (values.length === 0) return false;
			if (field === "application" && "miniTransactions" in e) {
				const minis =
					(
						e as Entry & {
							miniTransactions?: Array<{ application: string | null }>;
						}
					).miniTransactions ?? [];
				if (
					!minis.some(
						(mini) => mini.application && values.includes(mini.application),
					)
				)
					return false;
				continue;
			}
			const cell = ENUM_ACCESSORS[field](e);
			if (cell == null || !values.includes(cell)) return false;
		}

		return true;
	};
}
